(() => {
  if (window.__cgCorporateGiftingInit) return;
  window.__cgCorporateGiftingInit = true;

  const CATALOGUE_FILE_NAME = 'Nykaa-Wanderlust-Corporate-Catalogue.pdf';
  const EMAIL_PATTERN = /\S+@\S+\.\S+/;
  const TOAST_DURATION_MS = 3500;
  const SUBMIT_RESET_MS = 4000;

  const normalizeUrl = (url) => {
    if (!url) return '';
    let normalized = String(url).trim();
    if (normalized.startsWith('//')) normalized = `https:${normalized}`;
    return normalized;
  };

  const isHttpUrl = (url) =>
    url.startsWith('http://') || url.startsWith('https://');

  const forceDownload = async (url) => {
    try {
      const response = await fetch(url, { mode: 'cors', credentials: 'omit' });
      if (!response.ok) throw new Error('Failed to fetch catalogue PDF.');

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.setAttribute('download', CATALOGUE_FILE_NAME);
      anchor.hidden = true;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1500);
    } catch {
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.target = '_blank';
      anchor.rel = 'noopener noreferrer';
      anchor.setAttribute('download', CATALOGUE_FILE_NAME);
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
    }
  };

  const getField = (formRoot, name) =>
    formRoot.querySelector(`[data-cg="${name}"]`);

  const showToast = (toast, message, durationMs = TOAST_DURATION_MS) => {
    if (!toast) return;
    toast.textContent = message;
    toast.classList.add('cg-toast--show');
    window.clearTimeout(toast._cgToastTimer);
    toast._cgToastTimer = window.setTimeout(() => {
      toast.classList.remove('cg-toast--show');
    }, durationMs);
  };

  const validateEnquiryForm = (formRoot, toast) => {
    const requiredFields = [
      { name: 'name', label: 'Name' },
      { name: 'company', label: 'Company' },
      { name: 'email', label: 'Email', isEmail: true },
      { name: 'phone', label: 'Mobile number' },
      { name: 'quantity', label: 'Quantity' },
    ];

    for (const field of requiredFields) {
      const input = getField(formRoot, field.name);
      if (!input) continue;

      const value = input.value.trim();
      if (!value) {
        showToast(toast, `${field.label} required`);
        input.focus();
        return false;
      }

      if (field.isEmail && !EMAIL_PATTERN.test(value)) {
        showToast(toast, 'Valid email required');
        input.focus();
        return false;
      }
    }

    return true;
  };

  const sendEnquiry = async (sheetUrl, payload) => {
    if (!isHttpUrl(sheetUrl)) {
      throw new Error('missing-sheet-url');
    }

    await fetch(sheetUrl, {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  };

  const resetEnquiryForm = (formRoot) => {
    [
      'name',
      'company',
      'email',
      'phone',
      'delivery_location',
      'notes',
      'occasion',
      'quantity',
    ].forEach((name) => {
      const input = getField(formRoot, name);
      if (input) input.value = '';
    });
  };

  const initializeEnquiryForm = (formRoot) => {
    if (!formRoot || formRoot.dataset.cgFormBound === '1') return;
    formRoot.dataset.cgFormBound = '1';

    const submitButton = formRoot.querySelector('[data-cg-submit]');
    if (!submitButton) return;

    const toast =
      formRoot.parentElement?.querySelector('[data-cg-toast]') ?? null;
    const sheetUrl = (formRoot.getAttribute('data-sheet-url') || '').trim();
    const sourceLabel =
      formRoot.getAttribute('data-source-label') || 'Website Enquiry';
    const defaultLabel = submitButton.textContent;

    const handleSubmit = async (event) => {
      event.preventDefault();
      if (!validateEnquiryForm(formRoot, toast)) return;

      submitButton.textContent = 'Sending...';
      submitButton.disabled = true;

      try {
        await sendEnquiry(sheetUrl, {
          timestamp: new Date().toISOString(),
          source: sourceLabel,
          name: getField(formRoot, 'name')?.value.trim() || '',
          company: getField(formRoot, 'company')?.value.trim() || '',
          email: getField(formRoot, 'email')?.value.trim() || '',
          phone: getField(formRoot, 'phone')?.value.trim() || '',
          occasion: getField(formRoot, 'occasion')?.value || '',
          quantity: getField(formRoot, 'quantity')?.value || '',
          delivery_location:
            getField(formRoot, 'delivery_location')?.value.trim() || '',
          notes: getField(formRoot, 'notes')?.value.trim() || '',
        });

        submitButton.textContent = 'Sent';
        showToast(toast, 'Quote request sent! We\u2019ll respond within 4 hrs.');
        resetEnquiryForm(formRoot);
        window.setTimeout(() => {
          submitButton.textContent = defaultLabel;
          submitButton.disabled = false;
        }, SUBMIT_RESET_MS);
      } catch (error) {
        if (error?.message === 'missing-sheet-url') {
          showToast(toast, 'Sheet URL not configured in Theme Editor');
        } else {
          console.error(error);
          showToast(toast, 'Could not send. Please try again.');
        }
        submitButton.textContent = defaultLabel;
        submitButton.disabled = false;
      }
    };

    const formElement = formRoot.matches('form')
      ? formRoot
      : formRoot.querySelector('form');

    if (formElement) {
      formElement.addEventListener('submit', handleSubmit);
    } else {
      submitButton.addEventListener('click', handleSubmit);
    }
  };

  document.addEventListener(
    'click',
    (event) => {
      const downloadButton = event.target.closest('[data-cg-download-btn]');
      if (!downloadButton) return;

      const wrap = downloadButton.closest('[data-cg-download]');
      if (!wrap) return;

      event.preventDefault();
      const pdfUrl = normalizeUrl(wrap.getAttribute('data-pdf-url'));
      if (!isHttpUrl(pdfUrl)) {
        console.warn(
          'Corporate Gifting: no Catalogue PDF URL configured on this section.'
        );
        return;
      }

      downloadButton.disabled = true;
      forceDownload(pdfUrl).finally(() => {
        downloadButton.disabled = false;
      });
    },
    { passive: false }
  );

  const initializeAllEnquiryForms = () => {
    document.querySelectorAll('[data-cg-form]').forEach(initializeEnquiryForm);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAllEnquiryForms, {
      once: true,
    });
  } else {
    initializeAllEnquiryForms();
  }
})();
